<template>
  <div
    v-if="
      isReady && (selectedModal || blockingToast || nonBlockingToasts.length)
    "
    data-site-notification-manager
  >
    <SiteNotificationModal
      v-if="selectedModal"
      :notification="selectedModal"
      :logged-in="effectiveLoggedIn"
      @dismiss="dismiss(selectedModal.id)"
    />
    <SiteNotificationToastRegion
      v-if="blockingToast || nonBlockingToasts.length"
      :blocking-toast="blockingToast"
      :non-blocking-toasts="nonBlockingToasts"
      :collapsed="notificationStore.nonBlockingCollapsed"
      @dismiss="dismiss"
      @toggle-collapse="setCollapsed"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'

import { evaluateNotifications } from '~/utils/siteNotification'
import { useSiteNotificationStore } from '~/store/siteNotificationStore'
import type {
  NotificationAudienceContext,
  NotificationEvaluationResult,
  NotificationOrganizationInput,
  NotificationUserRoleInput,
  SiteNotification,
  SiteNotificationConditionMock,
} from '~/types/siteNotification'
import { getSiteNotificationConditionMockAccountId } from '~/utils/siteNotificationConditionMock'

const props = withDefaults(
  defineProps<{
    globalEntries?: readonly unknown[] | null
    pageEntries?: readonly unknown[] | null
    accountId?: string | null
    loggedIn?: boolean
    rolesRetrieved?: boolean
    // These values come from the existing tabletop payload. Keep the adapter
    // boundary structural because the auth payload is only partially typed.
    roles?: readonly unknown[] | null
    stores?: readonly unknown[] | null
    visitorLanguage?: string | null
    visitorCountry?: string | null
    routeSlug?: string | string[] | null
    referencesReady?: boolean
    /** Layout-only context override that keeps real notification entries. */
    conditionMock?: SiteNotificationConditionMock | null
    /** Locale selection must finish before notifications can be presented. */
    localeModalOpen?: boolean
  }>(),
  {
    globalEntries: () => [],
    pageEntries: () => [],
    accountId: null,
    loggedIn: false,
    rolesRetrieved: false,
    roles: () => [],
    stores: () => [],
    visitorLanguage: null,
    visitorCountry: null,
    routeSlug: null,
    referencesReady: true,
    conditionMock: null,
    localeModalOpen: false,
  },
)

const notificationStore = useSiteNotificationStore()
const mounted = ref(false)
const evaluation = ref<NotificationEvaluationResult>({
  candidates: [],
  warnings: [],
  errors: [],
})
const selectedModal = ref<SiteNotification | null>(null)
const selectedBlocking = ref<SiteNotification | null>(null)

const conditionMockEnabled = computed(() => Boolean(props.conditionMock))
const conditionMockTermsAcceptanceAt = computed<string | null | undefined>(
  () => {
    switch (props.conditionMock?.termsStatus) {
      case 'unsigned':
        return null
      case 'stale':
        return '2000-01-01T00:00:00.000Z'
      case 'current':
        return '2999-01-01T00:00:00.000Z'
      default:
        return undefined
    }
  },
)
const conditionMockStores = computed<NotificationOrganizationInput[]>(() => {
  const condition = props.conditionMock
  if (!condition) return []

  const storeIds = condition.storeIds.length
    ? condition.storeIds
    : ['dev-site-notification-condition-store']

  return storeIds.map((id) => ({
    id,
    country: condition.country,
    roles: [...condition.roles],
    ...(conditionMockTermsAcceptanceAt.value !== undefined
      ? {
          acceptedTermsAndConditionsAt: conditionMockTermsAcceptanceAt.value,
        }
      : {}),
  }))
})
const conditionMockRoles = computed<NotificationUserRoleInput[]>(() => {
  const condition = props.conditionMock
  if (!condition) return []

  return conditionMockStores.value.flatMap((store) =>
    condition.roles.map((roleName) => ({
      roleName,
      organization: {
        id: store.id,
        country: store.country,
        ...(conditionMockTermsAcceptanceAt.value !== undefined
          ? {
              acceptedTermsAndConditionsAt:
                conditionMockTermsAcceptanceAt.value,
            }
          : {}),
      },
    })),
  )
})
const effectiveAccountId = computed(() =>
  conditionMockEnabled.value
    ? getSiteNotificationConditionMockAccountId(props.conditionMock!)
    : props.accountId,
)
const effectiveLoggedIn = computed(() =>
  conditionMockEnabled.value ? true : props.loggedIn,
)
const effectiveRolesRetrieved = computed(() =>
  conditionMockEnabled.value ? true : props.rolesRetrieved,
)
const effectiveRoles = computed(() =>
  conditionMockEnabled.value ? conditionMockRoles.value : props.roles,
)
const effectiveStores = computed(() =>
  conditionMockEnabled.value ? conditionMockStores.value : props.stores,
)
const effectiveVisitorLanguage = computed(() =>
  conditionMockEnabled.value
    ? props.conditionMock?.locale
    : props.visitorLanguage,
)
const effectiveVisitorCountry = computed(() =>
  conditionMockEnabled.value
    ? props.conditionMock?.country
    : props.visitorCountry,
)

const hasVisitorLanguage = computed(
  () =>
    typeof effectiveVisitorLanguage.value === 'string' &&
    effectiveVisitorLanguage.value.trim(),
)
const hasVisitorCountry = computed(
  () =>
    typeof effectiveVisitorCountry.value === 'string' &&
    effectiveVisitorCountry.value.trim(),
)
const isReady = computed(
  () =>
    mounted.value &&
    (!effectiveLoggedIn.value ||
      (Boolean(effectiveAccountId.value?.trim()) &&
        effectiveRolesRetrieved.value === true)) &&
    Boolean(hasVisitorLanguage.value) &&
    Boolean(hasVisitorCountry.value) &&
    props.localeModalOpen !== true &&
    props.referencesReady === true,
)

const activeCandidates = computed(() =>
  evaluation.value.candidates.filter(
    (candidate) => !notificationStore.isDismissed(candidate.id),
  ),
)
const blockingToast = computed(() =>
  selectedBlocking.value?.displayType === 'Toast'
    ? selectedBlocking.value
    : null,
)
const nonBlockingToasts = computed(() =>
  activeCandidates.value.filter(
    (candidate) => candidate.displayType === 'Toast' && !candidate.blocking,
  ),
)

const emptyEvaluation = (): NotificationEvaluationResult => ({
  candidates: [],
  warnings: [],
  errors: [],
})

const selectPresentations = () => {
  const candidates = activeCandidates.value
  const blockingCandidates = candidates.filter(
    (candidate) => candidate.blocking,
  )

  // A previously consumed blocking slot cannot promote a replacement. If the
  // owner is still eligible, it is the only entry that may be shown there.
  const blocking = notificationStore.claimedBlockingId
    ? blockingCandidates.find(
        (candidate) => candidate.id === notificationStore.claimedBlockingId,
      ) || null
    : blockingCandidates[0] || null

  selectedBlocking.value = blocking

  // A blocking Modal owns both slots. A blocking Toast owns only the blocking
  // slot, so a non-blocking Modal may coexist with it.
  const blockingModal = blocking?.displayType === 'Modal' ? blocking : null
  const modal = blockingModal
    ? blockingModal
    : notificationStore.claimedModalId
      ? candidates.find(
          (candidate) =>
            candidate.id === notificationStore.claimedModalId &&
            candidate.displayType === 'Modal',
        ) || null
      : candidates.find(
          (candidate) =>
            candidate.displayType === 'Modal' && !candidate.blocking,
        ) || null

  selectedModal.value = modal
}

const claimRenderedSlots = async () => {
  if (!isReady.value) return

  // Vue has committed the selected component by this point. Claims are made
  // after that commit so a malformed or empty candidate cannot consume a slot.
  await nextTick()
  if (!isReady.value) return

  const blocking = selectedBlocking.value
  if (blocking && !notificationStore.claimedBlockingId) {
    notificationStore.claimBlocking(blocking.id)
  }

  const modal = selectedModal.value
  if (modal && !notificationStore.claimedModalId) {
    notificationStore.claimModal(modal.id)
  }
}

const runEvaluation = () => {
  if (!isReady.value) {
    evaluation.value = emptyEvaluation()
    selectedBlocking.value = null
    selectedModal.value = null
    return
  }

  // Hydration also replaces state from a previous account or anonymous session.
  // It does not create a sessionStorage record until presentation changes.
  notificationStore.hydrate(
    effectiveLoggedIn.value ? effectiveAccountId.value : null,
    !effectiveLoggedIn.value,
  )

  const context: NotificationAudienceContext = {
    authenticated: effectiveLoggedIn.value,
    ready: true,
    locale: effectiveVisitorLanguage.value,
    visitorCountry: effectiveVisitorCountry.value,
    stores: (effectiveStores.value || []) as NotificationOrganizationInput[],
    roles: (effectiveRoles.value || []) as NotificationUserRoleInput[],
  }

  let nextEvaluation: NotificationEvaluationResult
  try {
    nextEvaluation = evaluateNotifications({
      context,
      globalEntries: props.globalEntries,
      pageEntries: props.pageEntries,
      routeSlug: props.routeSlug,
    })
  } catch (error) {
    console.error('[site-notification] failed to evaluate notifications', error)
    evaluation.value = emptyEvaluation()
    selectedBlocking.value = null
    selectedModal.value = null
    return
  }

  // Empty content is a no-op. In particular, it should not create a new
  // account session record merely because a page has no registry entries.
  evaluation.value = nextEvaluation
  if (!nextEvaluation.candidates.length) {
    selectedBlocking.value = null
    selectedModal.value = null
    return
  }

  selectPresentations()
  void claimRenderedSlots()
}

const dismiss = (entryId: string) => {
  if (!isReady.value) return
  if (notificationStore.dismiss(entryId)) runEvaluation()
}

const setCollapsed = (collapsed: boolean) => {
  if (!isReady.value) return
  notificationStore.setNonBlockingCollapsed(collapsed)
}

watch(
  () => [
    props.globalEntries,
    props.pageEntries,
    props.accountId,
    props.loggedIn,
    props.rolesRetrieved,
    props.roles,
    props.stores,
    props.visitorLanguage,
    props.visitorCountry,
    props.routeSlug,
    props.referencesReady,
    props.conditionMock,
    props.localeModalOpen,
  ],
  runEvaluation,
  { immediate: true },
)

onMounted(() => {
  mounted.value = true
  runEvaluation()
})
</script>
